// SubmissionMutationHelpers.js 
 
 import { DataStore } from "@aws-amplify/datastore";
import { Submissions } from "../models";
import { FormTypes, ModelType, LogCategory } from "../models";
import { EventsSourceType } from "../Enums";
import { raiseEvent } from "./EventHelpers";

  /**
   * Updates file-related fields for a submission and updates the corresponding boolean field.
   * This function retrieves all submissions from DataStore, updates the specified file field, 
   * and manages associated reviews. It handles both adding new files and replacing/removing existing files.
   *
   * @param {String} rowId - ID of the submission to update.
   * @param {Object|null} newValue - New file object to add/update or null to remove.
   * @param {String} field - The field being updated, related to the file (e.g., "cv").
   * @param {FormTypes} formType - The type of form (e.g., SUBMISSION, REVIEW).
   * @param {Array} submissionTokens - Array of tokens to filter submissions.
   * @param {Array} reviewTokens - Array of tokens to filter reviews.
   * @throws {Error} If any operation during file handling fails.
   */
 

 export const updateFileFields = async (
    rowId,
    newValue,
    field,
    formType,
    accountToken,
    userId
  ) => {
    console.log("AppHelperFunctions: handleFileChange called for field:", field);

    // Fetch all submissions from DataStore
    let allSubmissions = [];
    try {
      allSubmissions = await DataStore.query(Submissions);
    } catch (error) {
      throw new Error(`Error fetching submissions from DataStore: ${error.message}`);
    }

    // Try to fetch the original record based on rowId
    let originalSubmission;
    try {
      originalSubmission = allSubmissions.find(submission => submission.id === rowId);
      if (!originalSubmission) {
        throw new Error("UFF: Couldn't find the submission to change file details");
      }
    } catch (error) {
      throw new Error(`Error finding submission: ${error.message}`);
    }

    // Define fieldPath and value for data.<field> based on newValue
    const fieldPath = `data.${field}`;
    const value = !!newValue; // true if newValue is provided, false if null

    // Use updateSubmissionFields to handle both the file update and the boolean in data.<field>
    let updatedSubmission;
    try {
      updatedSubmission = updateSubmissionFields(
        originalSubmission,
        fieldPath,
        value,
        field,
        newValue
      );
    } catch (error) {
      throw new Error(`UFF: updateSubmissionFields failed for submission ID ${originalSubmission.id}: ${error.message}`);
    }

    // Post-update validation
    if (updatedSubmission.data[field] !== value) {
      console.warn(`Warning: data.${field} should be ${value} but is ${updatedSubmission.data[field]}`);
    }

    if (formType === FormTypes.SUBMISSION && !validateSubmission(updatedSubmission)) {
      throw new Error("Updated submission contains non-serializable properties or is invalid.");
    }

    // Save the updated submission back to DataStore
    let savedSubmission;
    try {
      savedSubmission = await DataStore.save(updatedSubmission);
      if (formType === FormTypes.SUBMISSION) {
        await raiseSubmissionJsonPageUpdate(updatedSubmission, { accountToken, userId });
      }
    } catch (error) {
      throw new Error(`Error saving submissions or reviews to DataStore: ${error.message}`);
    }
    return savedSubmission;
  };

  /**
   * Save one or more field changes to a submission in a single atomic DataStore operation.
   *
   * @param {String} rowId - ID of the submission to update.
   * @param {Array<{field: string, value: any}>} patches - Fields to update. Each field is the
   *   dataSource string (e.g. "data.authors", "hidden"). Single-field saves pass a one-element array.
   * @param {FormTypes} formType
   * @param {String} accountToken - Current account token (threaded through to raiseSubmissionJsonPageUpdate).
   * @param {String} userId - Current admin user id (threaded through to raiseSubmissionJsonPageUpdate).
   * @returns {{ model: Submissions, baseVersion: number }} The saved model and the _version that was
   *   read before saving — used by the caller to detect AppSync confirmation via subscription.
   */
  export const updateSubmission = async (rowId, patches, formType, accountToken, userId) => {
    let allSubmissions;
    try {
      allSubmissions = await DataStore.query(Submissions);
    } catch (error) {
      throw new Error(`updateSubmission: DataStore query failed: ${error.message}`);
    }

    const originalSubmission = allSubmissions.find(s => s.id === rowId);
    if (!originalSubmission) {
      throw new Error(`updateSubmission: submission ${rowId} not found`);
    }

    const baseVersion = originalSubmission._version;

    // Build new data/meta objects from the original, then apply all patches in one copyOf.
    // Spreading from originalSubmission (not draft) avoids Immer proxy issues with AWSJSON fields.
    let updatedSubmission;
    try {
      const newData = { ...(originalSubmission.data || {}) };
      const newMeta = { ...(originalSubmission.meta || {}) };
      let dataChanged = false;
      let metaChanged = false;

      for (const { field, value } of patches) {
        const fieldPath =
          field === 'hidden' ? 'hidden'
          : field.startsWith('data.') || field.startsWith('meta.') ? field
          : `data.${field}`;

        if (fieldPath.startsWith('data.')) {
          newData[fieldPath.split('.')[1]] = value;
          dataChanged = true;
        } else if (fieldPath.startsWith('meta.')) {
          newMeta[fieldPath.split('.')[1]] = value;
          metaChanged = true;
        } else {
          // root-level field (e.g. hidden) — handled inside copyOf directly
          updatedSubmission = Submissions.copyOf(
            updatedSubmission || originalSubmission,
            draft => { draft[fieldPath] = value; }
          );
        }
      }

      // Apply data/meta replacements in one final copyOf
      if (dataChanged || metaChanged) {
        updatedSubmission = Submissions.copyOf(
          updatedSubmission || originalSubmission,
          draft => {
            if (dataChanged) draft.data = newData;
            if (metaChanged) draft.meta = newMeta;
          }
        );
      }

      if (!updatedSubmission) {
        throw new Error('updateSubmission: no patches produced a change');
      }
    } catch (error) {
      throw new Error(`updateSubmission: copyOf failed for ${rowId}: ${error.message}`);
    }

    if (!validateSubmission(updatedSubmission)) {
      throw new Error(`updateSubmission: updated submission is invalid for ${rowId}`);
    }

    let savedModel;
    try {
      savedModel = await DataStore.save(updatedSubmission);
      if (formType === FormTypes.SUBMISSION) {
        await raiseSubmissionJsonPageUpdate(updatedSubmission, { accountToken, userId });
      }
    } catch (error) {
      throw new Error(`updateSubmission: DataStore.save failed for ${rowId}: ${error.message}`);
    }

    return { model: savedModel, baseVersion };
  };

  /**
   * Helper function to update a submission, either by modifying files and corresponding data.<field>
   * or by updating a specific field.
   *
   * @param {Object} originalSubmission - The original submission object to be copied and updated.
   * @param {String} fieldPath - The field to update (e.g., "data.fieldName" or "meta.someField").
   * @param {any} value - The value to set for the specified field (boolean for files, other types for fields).
   * @param {String|null} dataKey - The dataKey associated with a file, if updating files, or null if only updating fields.
   * @param {Object|null} newFile - New file object to add to files array, or null to remove the file.
   * @returns {Object} - A new object representing the copied and updated submission.
   * @throws {Error} - If the update operation fails.
   */
  export const updateSubmissionFields = (originalSubmission, fieldPath, value, dataKey = null, newFile = null) => {
    try {
      return Submissions.copyOf(originalSubmission, (draft) => {
        // Conditionally update the files array if dataKey and newFile are provided
        if (dataKey !== null) {
          // Remove existing file(s) with the specified dataKey
          draft.files = (draft.files || []).filter(file => file.dataKey !== dataKey);

          // Add the new file if provided
          if (newFile) {
            draft.files.push({ dataKey, ...newFile });
          }

          // Deduplicate the files array based on dataKey
          const uniqueFiles = draft.files.reduce((acc, file) => {
            acc[file.dataKey] = file; // Keep only the last instance of each dataKey
            return acc;
          }, {});
          draft.files = Object.values(uniqueFiles);
        }

        // Update the specified field (root, data, or meta).
        // For AWSJSON fields (data/meta), replace the whole object to avoid Immer proxy mutation issues.
        if (fieldPath.startsWith("data.")) {
          const key = fieldPath.split('.')[1];
          draft.data = { ...(originalSubmission.data || {}), [key]: value };
        } else if (fieldPath.startsWith("meta.")) {
          const key = fieldPath.split('.')[1];
          draft.meta = { ...(originalSubmission.meta || {}), [key]: value };
        } else {
          draft[fieldPath] = value;
        }
      });
    } catch (error) {
      throw new Error(`updateSubmissionFields failed for submission ID ${originalSubmission.id}: ${error.message}`);
    }
  }; 

  /**
   * A submission no longer writes back into any review's `data.submission` (DataStore) —
   * it has exactly one S3 JSON page of its own. Instead of writing back to DataStore, this
   * raises a "JSONPageCreated" event so the backend Lambda regenerates that one page from
   * the updated submission. No review lookup involved — this fires unconditionally for every
   * submission-type update.
   *
   * Field names below are based on how "SubmissionsAssigned" builds its payload (which the
   * backend also uses to raise JSONPageCreated) — see buildAssignmentPayload/raiseAssignmentsEvent
   * in AssignmentHelpers.js: accountToken, projectToken, templateName, and templateFormToken are
   * all required there and forwarded as-is, so JSONPageCreated needs the same.
   *
   * @param {Object} updatedSubmission - The updated submission after changes.
   * @param {Object} ctx
   * @param {String} ctx.accountToken - Current account token (required by the event payload).
   * @param {String} ctx.userId - Current admin user id (required by raiseEvent for sourceId).
   */
  const raiseSubmissionJsonPageUpdate = async (updatedSubmission, { accountToken, userId } = {}) => {
    const submissionId = updatedSubmission?.id;
    if (!submissionId) return;

    if (!accountToken || !userId) {
      console.warn(
        "raiseSubmissionJsonPageUpdate: missing accountToken/userId — cannot raise JSONPageCreated event",
        { submissionId }
      );
      return;
    }

    const templateName = "submission_for_review_json"; // N.B. DC Hardcoded for now
    const templateFormToken = updatedSubmission.formToken; // N.B. DC Hardcoded for now

    try {
      await raiseEvent({
        event: "JSONPageCreated",
        sourceType: EventsSourceType.USER,
        sourceId: `${EventsSourceType.USER}:${userId}`,
        userId,
        projectToken: updatedSubmission.projectToken,
        payload: {
          sourceSubmissionId: submissionId,
          projectToken: updatedSubmission.projectToken,
          accountToken,
          templateName,
          templateFormToken,
          modelType: ModelType.SUBMISSION,
          category: LogCategory.CREATED,
          entityId: submissionId,
        },
      });
    } catch (error) {
      console.error(
        `raiseSubmissionJsonPageUpdate: failed to raise JSONPageCreated for submission ${submissionId}:`,
        error
      );
    }
  };

  /**
   * Validates a submission object, ensuring it is serializable and does not contain invalid properties.
   * Throws an error if the submission is null, not an object, or contains non-serializable properties.
   *
   * @param {Object} submission - The submission object to validate.
   * @returns {Boolean} - Returns true if the submission is valid.
   * @throws {Error} If the submission is null, not an object, or contains non-serializable properties.
   */
  const validateSubmission = (submission) => {
    if (!submission || typeof submission !== 'object') {
      console.warn("Invalid submission: Not an object or null.");
      throw new Error("Invalid submission: Not an object or null.");
    }

    const submissionId = submission.id; // Capture the id for better error reporting

    for (const key in submission) {
      if (typeof submission[key] === 'function' || submission[key] instanceof Event) {
        console.warn(`Non-serializable property found in submission: ${key} with value:`, submission[key]);
        throw new Error(`Non-serializable property found in submission ID ${submissionId}: ${key} with value: ${submission[key]}`);
      }
    }

    return true;
  };

